/**
 * useFIManagerData Hook
 *
 * Fetches per-F&I-manager performance data from /fi-manager-performance.
 * Refresh delegates to refreshSalesManagerData — the underlying loader writes
 * MANAGER#, SALESPERSON#, and FI_MANAGER# records in the same DynamoDB partition.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getFiManagerPerformance,
  refreshSalesManagerData,
  FiManagerPerformanceResponse,
} from '../services/api';
import {
  FiManager,
  FiManagerSummary,
  SortConfig,
} from '../components/forecast/FIManagerPerformance/types';

interface UseFIManagerDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean;
}

interface UseFIManagerDataResult {
  fiManagers: FiManager[];
  summary: FiManagerSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
}

function transformFiManager(raw: FiManagerPerformanceResponse['fiManagers'][0]): FiManager {
  return {
    fiManagerId: raw.fiManagerId,
    fiManagerName: raw.fiManagerName,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    totalUnits: raw.totalUnits,
    newUnits: raw.newUnits,
    usedUnits: raw.usedUnits,
    backEndGross: raw.backEndGross,
    financeReserve: raw.financeReserve,
    productGross: raw.productGross,
    serviceContractCount: raw.serviceContractCount,
    serviceContracts: raw.serviceContracts,
    pvr: raw.pvr ?? null,
    productMixPct: raw.productMixPct ?? null,
  };
}

export function useFIManagerData({
  locationId,
  year,
  month,
  enabled,
}: UseFIManagerDataParams): UseFIManagerDataResult {
  const [fiManagers, setFiManagers] = useState<FiManager[]>([]);
  const [summary, setSummary] = useState<FiManagerSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'backEndGross',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getFiManagerPerformance(locationId, year, month);

      if (response.success && response.data) {
        setFiManagers(response.data.fiManagers.map(transformFiManager));
        setSummary(response.data.summary ?? null);
      } else {
        setError(response.error || 'Failed to load F&I manager data');
        setFiManagers([]);
        setSummary(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setFiManagers([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setFiManagers([]);
      setSummary(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshSalesManagerData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh F&I manager data');
        return;
      }

      const response = await getFiManagerPerformance(locationId, year, month);
      if (response.success && response.data) {
        setFiManagers(response.data.fiManagers.map(transformFiManager));
        setSummary(response.data.summary ?? null);
      } else {
        setError(response.error || 'Failed to load updated F&I manager data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  const sortedFiManagers = useMemo(() => {
    if (!fiManagers.length) return [];

    return [...fiManagers].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.fiManagerName.localeCompare(b.fiManagerName);
      } else {
        const aVal = a[field] ?? 0;
        const bVal = b[field] ?? 0;
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [fiManagers, sortConfig]);

  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    fiManagers: sortedFiManagers,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
  };
}
